// =============================================================================
// MeuDriver — Batch O3: RescheduleRequestPanel
// Painel de solicitação e resposta a pedidos de reagendamento.
// Utilizado tanto no StudentBookingDetail quanto (futuramente) no detalhe do instrutor.
// =============================================================================

import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CalendarCheck, MessageSquare, CheckCircle2, XCircle, Clock } from 'lucide-react'
import {
  useRescheduleRequests,
  useRequestReschedule,
  useRespondReschedule,
  type RescheduleRequest,
} from '@/hooks/useReschedule'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

interface RescheduleRequestPanelProps {
  bookingId: string
  currentUserId: string
  /** profile_id do aluno do booking */
  studentId: string
  /** profile_id do instrutor do booking */
  instructorProfileId: string
  /** Se true: mostra botão "Solicitar reagendamento" */
  canRequest: boolean
}

// ---------------------------------------------------------------------------
// Badge de status
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: RescheduleRequest['status'] }) {
  if (status === 'approved') {
    return (
      <Badge className="bg-green-50 text-green-700 border-green-200 gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Aprovado
      </Badge>
    )
  }
  if (status === 'rejected') {
    return (
      <Badge className="bg-red-50 text-red-600 border-red-200 gap-1">
        <XCircle className="h-3 w-3" />
        Recusado
      </Badge>
    )
  }
  return (
    <Badge className="bg-amber-50 text-amber-700 border-amber-200 gap-1">
      <Clock className="h-3 w-3" />
      Aguardando resposta
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// RescheduleRequestPanel
// ---------------------------------------------------------------------------

export function RescheduleRequestPanel({
  bookingId,
  currentUserId,
  studentId,
  instructorProfileId,
  canRequest,
}: RescheduleRequestPanelProps) {
  const { data: requests, isLoading } = useRescheduleRequests(bookingId)
  const requestReschedule = useRequestReschedule()
  const respondReschedule = useRespondReschedule(bookingId)

  const [requestOpen, setRequestOpen] = useState(false)
  const [newDate, setNewDate] = useState('')
  const [newTime, setNewTime] = useState('')
  const [reason, setReason] = useState('')

  const pendingRequest = requests?.find((r) => r.status === 'pending')
  const hasPending = Boolean(pendingRequest)

  // Determina se o usuário atual é quem pode responder ao pedido pendente
  const canRespond =
    pendingRequest &&
    pendingRequest.requested_by !== currentUserId &&
    (currentUserId === studentId || currentUserId === instructorProfileId)

  function handleSubmitRequest() {
    if (!newDate || !newTime) return
    const newAt = `${newDate}T${newTime}:00`
    requestReschedule.mutate(
      { booking_id: bookingId, new_time: newAt, reason: reason || null },
      {
        onSuccess: () => {
          setRequestOpen(false)
          setNewDate('')
          setNewTime('')
          setReason('')
        },
      },
    )
  }

  function handleRespond(requestId: string, approved: boolean) {
    respondReschedule.mutate({ request_id: requestId, approved })
  }

  const today = format(new Date(), 'yyyy-MM-dd')

  return (
    <div className="space-y-4">
      {/* Botão para abrir modal de solicitação */}
      {canRequest && !hasPending && (
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-brand-600 border-brand-200 hover:bg-brand-50"
          onClick={() => setRequestOpen(true)}
          data-testid="btn-request-reschedule"
        >
          <CalendarCheck className="h-4 w-4" />
          Solicitar reagendamento
        </Button>
      )}

      {/* Lista de pedidos */}
      {isLoading ? (
        <Skeleton className="h-16 rounded-xl" />
      ) : requests && requests.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">Pedidos de reagendamento</p>
          {requests.map((req) => {
            const isRequester = req.requested_by === currentUserId
            const canRespond =
              !isRequester &&
              req.status === 'pending' &&
              (currentUserId === studentId || currentUserId === instructorProfileId)

            return (
              <div
                key={req.id}
                className={cn(
                  'rounded-xl border p-4 space-y-2',
                  req.status === 'pending'
                    ? 'border-amber-200 bg-amber-50/50'
                    : req.status === 'approved'
                      ? 'border-green-200 bg-green-50/50'
                      : 'border-gray-200 bg-gray-50/50',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">
                      {isRequester ? 'Você solicitou' : 'Pedido recebido'} —{' '}
                      {format(parseISO(req.created_at), "d 'de' MMM 'às' HH:mm", { locale: ptBR })}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      Novo horário:{' '}
                      {format(parseISO(req.new_scheduled_at), "EEEE, d 'de' MMM 'às' HH:mm", { locale: ptBR })}
                    </p>
                    {req.reason && (
                      <p className="text-xs text-muted-foreground flex items-start gap-1">
                        <MessageSquare className="h-3 w-3 shrink-0 mt-0.5" />
                        {req.reason}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={req.status} />
                </div>

                {/* Botões de resposta */}
                {canRespond && (
                  <div className="flex gap-2 pt-1">
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleRespond(req.id, true)}
                      disabled={respondReschedule.isPending}
                      data-testid="btn-approve-reschedule"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                      Aprovar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleRespond(req.id, false)}
                      disabled={respondReschedule.isPending}
                      data-testid="btn-reject-reschedule"
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1" />
                      Recusar
                    </Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : null}

      {/* Dialog: solicitar reagendamento */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-brand-600" />
              Solicitar reagendamento
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="reschedule-date">Nova data</Label>
                <Input
                  id="reschedule-date"
                  type="date"
                  min={today}
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  data-testid="reschedule-date-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reschedule-time">Horário</Label>
                <Input
                  id="reschedule-time"
                  type="time"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  data-testid="reschedule-time-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="reschedule-reason">Motivo (opcional)</Label>
              <Textarea
                id="reschedule-reason"
                placeholder="Informe o motivo do reagendamento..."
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="reschedule-reason-input"
              />
            </div>

            <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-100 rounded-lg p-2.5">
              A outra parte precisará aprovar este pedido. O horário da aula só será
              alterado após a aprovação.
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setRequestOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1"
                onClick={handleSubmitRequest}
                disabled={!newDate || !newTime || requestReschedule.isPending}
                data-testid="btn-submit-reschedule"
              >
                {requestReschedule.isPending ? 'Enviando...' : 'Enviar pedido'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
